import httpService from "./httpService";
import type { ProductNotificationMappingView, ProductNotificationPage, ProductNotificationSelections, ProductNotificationTarget } from "../types/productNotificationMappings";
import { isNotificationPortfolioId } from "../utils/productUpdateNotifications";
import { readMapping, readMappingPage } from "../utils/productNotificationContracts";

const BASE = "/v3/accounts/product-notification-mappings";
const ACTOR_AUTH = { requiresAuth: true, requestScope: "actor" as const, cache: "no-store" as const };
const productPath = (productId: number, productType: ProductNotificationTarget["productType"]): string => {
  if ((productType !== "PORTFOLIO" && productType !== "BASKET") || !isNotificationPortfolioId(productId)) throw new Error("Invalid portfolio ID");
  return `${BASE}/${productType}/${productId}`;
};

/** Personal settings always belong to the actor, including in a delegated account. */
class ProductNotificationMappingsService {
  async list(page = 1, signal?: AbortSignal): Promise<ProductNotificationPage<ProductNotificationMappingView>> {
    const response = await httpService.get<ProductNotificationPage<ProductNotificationMappingView>>(
      `${BASE}?page=${page}&pageSize=25&includeBaskets=true`, { ...ACTOR_AUTH, signal }
    );
    return readMappingPage(response.data);
  }

  async get(productId: number, signal?: AbortSignal, productType: ProductNotificationTarget["productType"] = "PORTFOLIO"): Promise<ProductNotificationMappingView> {
    const response = await httpService.get<ProductNotificationMappingView>(productPath(productId, productType), { ...ACTOR_AUTH, signal });
    return readMapping(response.data, { productType, productId });
  }

  async upsert(productId: number, selections: ProductNotificationSelections, productType: ProductNotificationTarget["productType"] = "PORTFOLIO"): Promise<ProductNotificationMappingView> {
    // Do not forward response metadata, SMS, identity, or the UI gate to this strict API.
    const { shouldEmail, shouldWebInbox, shouldMobileInbox, shouldPush } = selections;
    const response = await httpService.put<ProductNotificationMappingView>(productPath(productId, productType),
      { shouldEmail, shouldWebInbox, shouldMobileInbox, shouldPush }, ACTOR_AUTH);
    return readMapping(response.data, { productType, productId });
  }

  async remove(productId: number, productType: ProductNotificationTarget["productType"] = "PORTFOLIO"): Promise<void> {
    await httpService.delete<unknown>(productPath(productId, productType), ACTOR_AUTH);
  }
}

export default new ProductNotificationMappingsService();
